import { useState, useEffect } from 'react'
import { api } from '../lib/api'

interface PricingItem {
  id: string
  itemType: string
  price: number
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  'gift_adult_male': 'Adult Male Gift',
  'gift_adult_female': 'Adult Female Gift',
  'gift_child_5_under': 'Child Gift (5 and under)',
  'gift_child_6_over': 'Child Gift (6 and over)',
  'gift_pet_cat': 'Pet Cat Gift',
  'gift_pet_dog': 'Pet Dog Gift',
  'gift_foodbank': 'Foodbank Donation',
  'santa_per_child': 'Santa Visit (per child)',
  'tokens': 'Activity Tokens',
}

export function PricingPage() {
  const [pricing, setPricing] = useState<PricingItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingPrice, setEditingPrice] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadPricing()
  }, [])

  async function loadPricing() {
    try {
      setLoading(true)
      setError(null)
      const data = await api.getAdminPricing()
      setPricing(data)
    } catch (err) {
      setError('Failed to load pricing')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  function startEditing(item: PricingItem) {
    setEditingId(item.id)
    setEditingPrice(item.price.toString())
  }

  function cancelEditing() {
    setEditingId(null)
    setEditingPrice('')
  }

  async function savePrice(id: string) {
    const price = parseFloat(editingPrice)

    if (isNaN(price) || price < 0) {
      setError('Please enter a valid price')
      return
    }

    try {
      setSaving(true)
      setError(null)

      await api.updateAdminPricing(id, price)

      // Update local state
      setPricing(pricing.map(p => p.id === id ? { ...p, price } : p))

      setEditingId(null)
      setEditingPrice('')
    } catch (err) {
      setError('Failed to update price')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading pricing...</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Pricing Management</h1>

      {error && (
        <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price (£)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pricing.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {ITEM_TYPE_LABELS[item.itemType] || item.itemType}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {editingId === item.id ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingPrice}
                      onChange={(e) => setEditingPrice(e.target.value)}
                      aria-label={`Price for ${ITEM_TYPE_LABELS[item.itemType] || item.itemType}`}
                      className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    `£${item.price.toFixed(2)}`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingId === item.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => savePrice(item.id)}
                        disabled={saving}
                        className="min-h-[44px] px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 transition-colors disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        disabled={saving}
                        className="min-h-[44px] px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEditing(item)}
                      className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>All prices are in GBP (£). Changes take effect immediately for new orders.</p>
      </div>
    </div>
  )
}
